// seatRequest — Change Run Mode Handler
//
// หน้าเว็บ (static ใน public/seat/, select-run-mode, ปุ่มเปลี่ยนโหมด POS) เขียน
// localStorage["seatChangeRequest"] แล้ว location.replace("/") — plugin นี้ทำต่อตอน boot
// ก่อนแอป mount จึงไม่ต้อง reload ซ้ำอีกรอบ:
//
//   โหมดที่ไม่ใช้ที่นั่ง (unassigned / terminal / audit) → คืนที่นั่งของเครื่องนี้
//   Main   + takeOver → เขียน id ตัวเองทับ (+ จด kicks ให้เครื่องเดิมเห็นว่าใครแย่ง)
//   Second + kick     → สลับกับเครื่องนั้นใน transaction เดียว (+ จด kicks)
//   Main / Second เฉย ๆ → ไม่เขียนอะไร ให้ seatMain / seatSecond จองหรือถามเอง
//   แล้วตั้งโหมดตาม request
//
// เขียน RTDB ไม่ได้ (ออฟไลน์) ก็ยังตั้งโหมดไปก่อน seatMain / seatSecond ตัดสินซ้ำให้
// หน้า login / app1 ของร้าน beta → ไม่แตะ request (ดู decideSkipSeatCheck)
import type { Router } from "vue-router";
import "../../../firebaseInstance";
import { RunMode } from "../../../interface/Pos";
import { resolveStoreId } from "../../core/composables/useStoreIdentity";
import { useRunningMode } from "../../core/composables/useRunningMode";
import { isDevReadOnly } from "../../core/utils/devStoreOverride";
import { SEAT_MODE_MAIN, SEAT_MODE_SECOND } from "../utils/seatRules";
import {
  DEVICE_BLOCKED_KEY,
  SEAT_CONFLICT_KEY,
  SEAT_LOST_KEY,
  SEAT_REQUEST_KEY,
  hasKey,
  parseSeatRequest,
  removeKey,
  takeRaw,
} from "../utils/seatStorage";
import { claimSeat, releaseSeats, swapSecond, waitForConnected, CONNECT_WAIT_MS } from "../services/seatRtdb";
import { getSeatIdentity } from "../services/seatGuard";
import { shouldSkipSeatCheck } from "../services/seatScope";

export default defineNuxtPlugin({
  name: "seat-request",
  parallel: false,
  async setup(nuxtApp) {
    // จับไว้ก่อน await แรก — หลัง await ไม่อยู่ใน nuxt context แล้ว
    const router = nuxtApp.$router as Router;
    try {
      // เครื่องโดนบล็อก — seatBlock พาไปหน้า blocked แล้ว ไม่ทำอะไรต่อ
      if (hasKey(DEVICE_BLOCKED_KEY)) return;
      if (!hasKey(SEAT_REQUEST_KEY)) return;

      const storeId = await resolveStoreId();
      if (!storeId) return; // ยังไม่ login — เก็บ request ไว้ (หมดอายุเองใน 10 นาที)
      // ไม่ใช้ request รอบนี้ — เก็บไว้เหมือนกัน (หมดอายุเอง)
      if (await shouldSkipSeatCheck(storeId, router)) return;

      // ใช้ครั้งเดียว — ลบก่อนทำ กันวนถ้ามีอะไรพังกลางทาง
      const request = parseSeatRequest(takeRaw(SEAT_REQUEST_KEY), {
        storeId,
        now: Date.now(),
        validModes: Object.values(RunMode),
      });
      if (!request) return;

      // เริ่มเรื่องใหม่ ข้อมูลหน้า static รอบก่อนไม่ใช้แล้ว
      removeKey(SEAT_CONFLICT_KEY);
      removeKey(SEAT_LOST_KEY);

      const identity = getSeatIdentity(storeId);
      // dev สวมร้านอื่น = ดูอย่างเดียว ห้ามแตะที่นั่งร้านจริง
      if (identity && !isDevReadOnly()) {
        await applyToSeats(storeId, identity, request, useRunningMode().getMode());
      }

      if (request.mode === RunMode.UNASSIGNED) useRunningMode().clear();
      else useRunningMode().setMode(request.mode);
    } catch (error) {
      // พังยังไงก็ห้ามทำให้แอปเปิดไม่ขึ้น
      console.warn("[seatRequest] ทำตาม request ไม่สำเร็จ", error);
    }
  },
});

async function applyToSeats(
  storeId: string,
  identity: { me: string; name: string },
  request: { mode: string; takeOver?: boolean; kick?: string | null },
  previousMode: string
) {
  const { me, name } = identity;
  const isSeatMode = (mode: string) => mode === SEAT_MODE_MAIN || mode === SEAT_MODE_SECOND;

  // ไปโหมดที่ไม่ใช้ที่นั่ง หรือสลับ Main ↔ Second → คืนที่เดิมก่อน
  // (ถ้าที่ใหม่เต็มแล้วไปค้างหน้าถาม ที่เดิมจะได้ไม่ค้างกินโควตาไว้)
  if (!isSeatMode(request.mode) || (isSeatMode(previousMode) && previousMode !== request.mode)) {
    await releaseSeats(storeId, me);
  }
  if (!isSeatMode(request.mode)) return;

  const wantsTakeOver = request.mode === SEAT_MODE_MAIN && request.takeOver;
  const wantsKick = request.mode === SEAT_MODE_SECOND && request.kick;
  if (!wantsTakeOver && !wantsKick) return;

  if (!(await waitForConnected(CONNECT_WAIT_MS))) {
    console.warn("[seatRequest] ออฟไลน์ แย่งที่นั่งไม่ได้ ให้ seatMain / seatSecond ตัดสินตอนต่อติด");
    return;
  }

  try {
    if (wantsTakeOver) {
      await claimSeat(storeId, { mode: SEAT_MODE_MAIN, me, name, quota: 0, force: true });
    } else if (wantsKick) {
      const result = await swapSecond(storeId, { me, name, kickId: request.kick as string });
      // เครื่องนั้นออกไปแล้ว — seatSecond จองตามโควตาให้เอง
      if (result === "not_found") console.info("[seatRequest] เครื่องที่จะแย่งออกไปแล้ว");
    }
  } catch (error) {
    console.warn("[seatRequest] แย่งที่นั่งไม่สำเร็จ", error);
  }
}
